import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUniversalAccess, faTextHeight, faAdjust, faFont, faEye } from '@fortawesome/free-solid-svg-icons';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [fontSize, setFontSize] = useState(100);
  const [isGrayscale, setIsGrayscale] = useState(false);
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [isReadableFont, setIsReadableFont] = useState(false);

  // Apply changes to the document
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    // Font Size
    html.style.fontSize = `${fontSize}%`;

    // Grayscale
    if (isGrayscale) {
      html.style.filter = 'grayscale(100%)';
    } else {
      html.style.filter = 'none';
    }

    // High Contrast
    if (isHighContrast) {
      body.classList.add('high-contrast');
    } else {
      body.classList.remove('high-contrast');
    }

    // Readable Font
    if (isReadableFont) {
      body.classList.add('readable-font');
    } else {
      body.classList.remove('readable-font');
    }

  }, [fontSize, isGrayscale, isHighContrast, isReadableFont]);

  // Inject global styles for the widget features
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      /* High Contrast Mode - Specific Element Overrides */
      .high-contrast img {
        filter: contrast(150%);
      }

      /* Ensure links are distinct in High Contrast */
      .high-contrast a {
        text-decoration: underline !important;
        color: #00FFFF !important;
      }

      /* Ensure buttons have borders in High Contrast */
      .high-contrast button,
      .high-contrast .btn,
      .high-contrast input,
      .high-contrast select,
      .high-contrast textarea {
        border: 2px solid #FFFF00 !important;
        background-color: #000000 !important;
        color: #FFFF00 !important;
      }

      /* Force borders on cards to maintain layout structure */
      .high-contrast .card,
      .high-contrast .project-card,
      .high-contrast .publication-card,
      .high-contrast .certificate-card,
      .high-contrast .resume-paper,
      .high-contrast .skill-box,
      .high-contrast .cta-card,
      .high-contrast .box-shadow-full,
      .high-contrast .rounded-lg {
        border: 2px solid #FFFF00 !important;
      }

      /* Fix specific biography quote box */
      .high-contrast .quote-box {
        border: 2px solid #FFFF00 !important;
        border-left-width: 4px !important;
      }

      .readable-font, .readable-font * {
        font-family: Arial, Helvetica, sans-serif !important;
        letter-spacing: 0.05em !important;
        line-height: 1.6 !important;
      }

      .a11y-widget-btn {
        position: fixed;
        bottom: 20px;
        left: 20px; /* Left side to avoid conflict with chat widgets/scroll to top */
        z-index: 10000;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: #4C5FD7;
        color: white;
        border: 2px solid transparent;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
        transition: transform 0.3s ease;
      }

      .a11y-widget-btn:hover {
        transform: scale(1.1);
      }

      .a11y-panel {
        position: fixed;
        bottom: 80px;
        left: 20px;
        z-index: 10000;
        background: white;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 5px 20px rgba(0,0,0,0.2);
        width: 250px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        animation: slideUp 0.3s ease-out;
      }

      .dark .a11y-panel {
        background: #1e293b;
        color: white;
        border: 1px solid #334155;
      }

      @keyframes slideUp {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .a11y-option {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        border-radius: 8px;
        cursor: pointer;
        transition: background 0.2s;
      }

      .a11y-option:hover {
        background-color: rgba(0,0,0,0.05);
      }
      .dark .a11y-option:hover {
        background-color: rgba(255,255,255,0.1);
      }

      .a11y-option.active {
        background-color: #e0e7ff;
        color: #4C5FD7;
      }
      .dark .a11y-option.active {
        background-color: #312e81;
        color: #818cf8;
      }

      .a11y-label {
        font-size: 14px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .a11y-controls {
        display: flex;
        gap: 5px;
      }

      .a11y-btn-sm {
        padding: 4px 8px;
        border-radius: 4px;
        border: 1px solid #ccc;
        background: transparent;
        cursor: pointer;
        font-weight: bold;
      }
      .dark .a11y-btn-sm {
        border-color: #475569;
        color: white;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  return (
    <>
      <button
        className="a11y-widget-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Accessibility Menu"
        aria-expanded={isOpen}
      >
        <FontAwesomeIcon icon={faUniversalAccess} />
      </button>

      {isOpen && (
        <div className="a11y-panel" role="dialog" aria-label="Accessibility Options">
          <div className="flex justify-between items-center mb-2 border-b pb-2 dark:border-gray-700">
            <h3 className="font-bold text-lg">Accessibility</h3>
            <button onClick={() => setIsOpen(false)} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400">Close</button>
          </div>

          {/* Font Size */}
          <div className="a11y-option">
            <div className="a11y-label">
              <FontAwesomeIcon icon={faTextHeight} /> Text Size
            </div>
            <div className="a11y-controls">
              <button className="a11y-btn-sm" onClick={() => setFontSize(f => Math.max(80, f - 10))} aria-label="Decrease text size">-</button>
              <span className="text-sm font-mono w-8 text-center">{fontSize}%</span>
              <button className="a11y-btn-sm" onClick={() => setFontSize(f => Math.min(150, f + 10))} aria-label="Increase text size">+</button>
            </div>
          </div>

          {/* Grayscale */}
          <button
            className={`a11y-option ${isGrayscale ? 'active' : ''}`}
            onClick={() => setIsGrayscale(!isGrayscale)}
          >
            <div className="a11y-label">
              <FontAwesomeIcon icon={faAdjust} /> Grayscale
            </div>
            <div className={`w-8 h-4 rounded-full relative transition-colors border border-transparent ${isGrayscale ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
              <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform border border-transparent ${isGrayscale ? 'left-4.5' : 'left-0.5'}`} style={{ left: isGrayscale ? '18px' : '2px' }}></div>
            </div>
          </button>

          {/* High Contrast */}
          <button
            className={`a11y-option ${isHighContrast ? 'active' : ''}`}
            onClick={() => setIsHighContrast(!isHighContrast)}
          >
            <div className="a11y-label">
              <FontAwesomeIcon icon={faEye} /> High Contrast
            </div>
            <div className={`w-8 h-4 rounded-full relative transition-colors border border-transparent ${isHighContrast ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
              <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform border border-transparent`} style={{ left: isHighContrast ? '18px' : '2px' }}></div>
            </div>
          </button>

          {/* Readable Font */}
          <button
            className={`a11y-option ${isReadableFont ? 'active' : ''}`}
            onClick={() => setIsReadableFont(!isReadableFont)}
          >
            <div className="a11y-label">
              <FontAwesomeIcon icon={faFont} /> Readable Font
            </div>
            <div className={`w-8 h-4 rounded-full relative transition-colors border border-transparent ${isReadableFont ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
              <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform border border-transparent`} style={{ left: isReadableFont ? '18px' : '2px' }}></div>
            </div>
          </button>

          <button
            className="mt-2 text-xs text-center text-red-500 hover:underline w-full"
            onClick={() => {
              setFontSize(100);
              setIsGrayscale(false);
              setIsHighContrast(false);
              setIsReadableFont(false);
            }}
          >
            Reset Settings
          </button>
        </div>
      )}
    </>
  );
};

export default AccessibilityWidget;
